import { useState } from 'react';
import { sendToHubSpot } from '@/utils/hubspot';

export const Footer = () => {
	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [subscriptionStatus, setSubscriptionStatus] = useState<
		'idle' | 'success' | 'error'
	>('idle');
	const [errorMessage, setErrorMessage] = useState('');

	const handleNewsletterSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!email) return;

		setIsSubmitting(true);
		setSubscriptionStatus('idle');
		setErrorMessage('');

		try {
			// Send to HubSpot - using list ID 6 for newsletter subscribers
			// (adjust the list ID based on your actual HubSpot setup)
			await sendToHubSpot(
				{
					properties: [
						{ property: 'email', value: email },
						{ property: 'firstname', value: firstName },
					],
				},
				7, // Newsletter subscribers list ID
			);

			setSubscriptionStatus('success');
			setEmail('');
			setFirstName('');
		} catch (error) {
			console.error('Newsletter subscription failed:', error);
			setSubscriptionStatus('error');
			setErrorMessage('Subscription failed. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<footer className='bg-neutral-800 w-full py-16 px-8'>
			<div className='container mx-auto max-w-7xl'>
				<div className='grid grid-cols-1 md:grid-cols-4 gap-8'>
					{/* Column 1: Logo and About */}
					<div className='col-span-1'>
						<img
							src='https://cdn.builder.io/api/v1/image/assets/17cfa75daf82417693353b2f7dd2694b/04c7bd764b0103198e817a43f06a247521a4ba4bff97ab67ac79996710c5d4bb?placeholderIfAbsent=true'
							alt='YAAP Logo'
							className='w-28 h-auto mb-6'
						/>
						<p className='text-[#8F9FA3] mb-10'>
							<span className='font-bold'>Empowering</span> you to discover your
							own voice; awakening the inherent confidence within.
						</p>

						{/* Social Media Icons */}
						<div className='flex gap-3 mt-4'>
							<a
								href='https://www.instagram.com/yaap_africa?igsh=Z2h0NHlveXd1Mzlu&utm_source=qr'
								target='_blank'
								rel='noopener'
								className='bg-white flex min-h-12 items-center justify-center w-12 h-12 rounded-full'
							>
								<img
									src='https://img.icons8.com/?size=100&id=32320&format=png&color=1A1A1A'
									alt='Instagram'
									className='w-[25px]'
								/>
							</a>
							<a
								href='https://www.tiktok.com/@yaap_africa?_t=ZM-8uhfSfSOOAB&_r=1'
								target='_blank'
								rel='noopener'
								className='bg-white flex min-h-12 items-center justify-center w-12 h-12 rounded-full'
							>
								<img
									src='https://img.icons8.com/?size=100&id=soupkpLfTkZi&format=png&color=1A1A1A'
									alt='Social Media'
									className='w-[25px]'
								/>
							</a>
						</div>
					</div>

					{/* Column 2: Navigation Links */}
					<div className='col-span-1'>
						<h3 className='text-white text-xl font-medium mb-6'>Quick Links</h3>
						<nav className='flex flex-col space-y-4 text-[#8F9FA3]'>
							<a href='/about' className='hover:text-white transition-colors'>
								About Us
								<span style={{ fontWeight: 600, color: 'rgba(251,176,64,1)' }}>
									.
								</span>
							</a>
							<a
								href='/services'
								className='hover:text-white transition-colors'
							>
								Services
								<span style={{ fontWeight: 600, color: 'rgba(251,176,64,1)' }}>
									.
								</span>
							</a>
							<a href='/blog' className='hover:text-white transition-colors'>
								Blog
								<span style={{ fontWeight: 600, color: 'rgba(251,176,64,1)' }}>
									.
								</span>
							</a>
							<a href='/contact' className='hover:text-white transition-colors'>
								Contact
								<span style={{ fontWeight: 600, color: 'rgba(251,176,64,1)' }}>
									.
								</span>
							</a>
						</nav>
					</div>

					{/* Column 3: Contact Information */}
					<div className='col-span-1'>
						<h3 className='text-white text-xl font-medium mb-6'>Contact Us</h3>
						<div className='text-[#8F9FA3] space-y-4'>
							{/* <p>+27 77 494 0231</p> */}
							<p>info@yaap.co.za</p>
						</div>

						<h3 className='text-white text-xl font-medium mt-10 mb-6'>
							Location
						</h3>
						<p className='text-[#8F9FA3]'>Johannesburg, South Africa</p>
					</div>

					{/* Column 4: Newsletter Signup */}
					<div className='col-span-1'>
						{/* <h3 className='text-white text-xl font-medium mb-6'>Newsletter</h3> */}
						<p className='text-white mb-4'>
							Stay updated with our latest news and offers.
						</p>
						{subscriptionStatus === 'success' ? (
							<div className='bg-green-900/30 border border-green-700/50 rounded-md p-4 text-green-300'>
								<p>Thank you for subscribing!</p>
								<p className='text-sm mt-1'>You'll receive our updates soon.</p>
							</div>
						) : (
							<form className='space-y-4' onSubmit={handleNewsletterSubmit}>
								<input
									type='text'
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									placeholder='Your first name'
									className='w-full px-4 py-2 bg-neutral-700 text-white border border-neutral-600 rounded-md focus:outline-none focus:ring-1 focus:ring-[#FBB040]'
									required
								/>
								<input
									type='email'
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder='Your email address'
									className='w-full px-4 py-2 bg-neutral-700 text-white border border-neutral-600 rounded-md focus:outline-none focus:ring-1 focus:ring-[#FBB040]'
									required
								/>

								{subscriptionStatus === 'error' && (
									<p className='text-red-400 text-sm'>{errorMessage}</p>
								)}
								<button
									type='submit'
									disabled={isSubmitting}
									className='w-full bg-[#FBB040] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#e9a43b] transition-colors disabled:opacity-70'
								>
									{isSubmitting ? 'Subscribing...' : 'Subscribe'}
								</button>
							</form>
						)}
					</div>
				</div>

				{/* Bottom footer with copyright and terms */}
				<div className='border-t border-neutral-700 mt-12 pt-8 flex flex-col md:flex-row justify-between text-[#8F9FA3] text-sm'>
					<div>© 2025 — Copyright All Rights reserved</div>
					<div className='mt-4 md:mt-0'>
						<a href='/privacy' className='font-bold hover:text-white'>
							Privacy
						</a>{' '}
						|{' '}
						<a href='/terms' className='font-bold hover:text-white'>
							Terms of use
						</a>
					</div>
				</div>
			</div>
		</footer>
	);
};
